import { PartnershipsProvider } from '@skyslope/partnerships-sdk/react';
import { useSelector } from 'react-redux';
import { IRootState } from '../store';
import React, { createContext, useContext, useMemo, useState } from 'react';
import useSessionStorage from '../hooks/useSessionStorage';
import { LoadAdsParams, PartnerAdAudience } from '@skyslope/partnerships-sdk';

interface AdContextType {
  adDismissedCollection: string | null;
  setAdDismissedCollection: (value: string) => void;
  adVisibleCollection: { [key: string]: boolean };
  setAdVisibleCollection: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
}

const AdContext = createContext<AdContextType | undefined>(undefined);

export const useSessionStorageAdContext = (): AdContextType => {
  const context = useContext(AdContext);
  if (!context) {
    throw new Error('useAdContext must be used within a AdProvider');
  }
  return context;
};

export enum AdPlacementIds {
  SIGNING = 'ds-signing',
  SIGNED = 'ds-signed',
}

const audienceTypeMap: { [key: string]: PartnerAdAudience } = {
  seller: PartnerAdAudience.Seller,
  buyer: PartnerAdAudience.Buyer,
};

const useEnvelopeData = () => {
  return useSelector((state: IRootState) => state.envelope.envelope);
};

const DigiSignAdsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const envelope = useEnvelopeData();
  const [adDismissedCollection, setAdDismissedCollection] = useSessionStorage('ad-revealed-collection');
  const [adVisibleCollection, setAdVisibleCollection] = useState<{ [key: string]: boolean }>({});
  const audience = envelope?.signer?.metadata?.['audienceContactType']?.toLowerCase();

  const loadAdParams = useMemo((): LoadAdsParams => {
    const audienceType = audience ? audienceTypeMap[audience] : PartnerAdAudience.Agent;

    const items = {
      regions: envelope?.metadata?.ownerRegionCountries?.split(',').map(region => region?.trim()),
      subscriberId: envelope?.metadata?.ownerSubscriberId,
      audience: audienceType,
    };

    return items;
  }, [envelope]);

  const loaded = loadAdParams.audience != null && loadAdParams.regions != null && loadAdParams.subscriberId != null;
  const environment = process.env.NODE_ENV === 'development' ? 'dev' : 'production';

  return (
    <AdContext.Provider
      value={{
        adDismissedCollection,
        setAdDismissedCollection,
        adVisibleCollection,
        setAdVisibleCollection,
      }}
    >
      <PartnershipsProvider environment={environment} loadAdsParams={loadAdParams} ready={loaded}>
        {children}
      </PartnershipsProvider>
    </AdContext.Provider>
  );
};

export default DigiSignAdsProvider;
